import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import React, { useState } from 'react';
import { RTW_FAQ_LINK } from 'src/config/ExternalLinks';
import { TaskConflict } from 'src/services/jmr/types';
import { Modification } from 'src/types/modification';

interface ApplyModificationsModalProps {
  conflicts: TaskConflict[];
  id: string;
  name: string;
  isOpen: boolean;
  onCancel: () => void;
  onSaveAndApply: (modifiedConflicts: TaskConflict[]) => void;
}
const ApplyModificationsModal = ({
  conflicts,
  name,
  isOpen,
  onCancel,
  onSaveAndApply,
}: ApplyModificationsModalProps) => {
  const [checkboxState, setCheckboxState] = useState<Record<string, boolean>>({});

  const isValid = () => conflicts.every((conflict) => hasBeenModified(conflict));

  const hasBeenModified = (conflict: TaskConflict) =>
    conflict.availableModifications.some((modification) => checkboxState[hashCheckbox(conflict, modification)]);

  const hashCheckbox = ({ taskId }: TaskConflict, { id }: Modification) => `${taskId}-${id}`;

  const getModifiedConflicts = () =>
    conflicts.map((conflict) => ({
      ...conflict,
      appliedModifications: conflict.availableModifications.filter(
        (modification) => checkboxState[hashCheckbox(conflict, modification)],
      ),
    }));

  return (
    <Modal
      title={`Modify: ${name}`}
      open={isOpen}
      onClose={onCancel}
      closeLabel="Close"
      aria-describedby="modal-description"
      width="700px"
      scrollContainer="modal"
    >
      <div>
        <Text type="b300">
          Please make the necessary modifications below to make the job an eligible match for the associate. Modifying a
          job will apply a 180 day limit to the job placement. Missing modification? Please submit a{' '}
          <Link target="_blank" href={RTW_FAQ_LINK}>
            SIM ticket
          </Link>
          .
        </Text>
      </div>
      <Column spacingInset={'400'}>
        {conflicts.map((conflict) => (
          <Box key={conflict.taskId} spacingInset={'400'} type="outline">
            <Row alignmentHorizontal="justify">
              <Text type="h100">Requirement: {conflict.taskName}</Text>
              <Tag type={hasBeenModified(conflict) ? 'success' : 'warning'}>
                {hasBeenModified(conflict) ? 'Modified' : 'Conflict'}
              </Tag>
            </Row>
            <Text>
              <strong>Conflict: </strong>
              {`Associate cannot ${conflict.taskName}${conflict.restrictedValue ? ` (${conflict.restrictedValue} Hours)` : ''}`}
            </Text>
            <Column spacing={'200'} spacingInset={'400 none none none'}>
              <Text type="h100">Select a Modification</Text>
              {conflict.availableModifications?.map((modification, i) => (
                <Row key={i}>
                  <Checkbox
                    checked={checkboxState[hashCheckbox(conflict, modification)]}
                    aria-label={modification.name}
                    onChange={(isChecked) =>
                      setCheckboxState({ ...checkboxState, [hashCheckbox(conflict, modification)]: isChecked })
                    }
                  />
                  <Text type="b300">{modification.name}</Text>
                </Row>
              ))}
            </Column>
          </Box>
        ))}
      </Column>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button type="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="primary" disabled={!isValid()} onClick={() => onSaveAndApply(getModifiedConflicts())}>
            Apply & Select
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default ApplyModificationsModal;
